/**
 * ChapterItem - 章節列表項目
 */

import { Link } from 'react-router-dom';
import type { ChapterItemProps } from './types';

export function ChapterItem({
  chapter,
  novelId,
  isLastRead,
  isAdmin,
  isUploadedNovel,
  isEditMode,
  isSelected,
  onEditContent,
  onRecrawl,
  onSkip,
  onMoveUp,
  onMoveDown,
  onDelete,
  onToggleSelect,
  isRecrawling,
  isSkipping,
  isFirst,
  isLast,
}: ChapterItemProps) {
  const isFailed = chapter.status === 'failed';
  const isCrawling = chapter.status === 'crawling' || chapter.status === 'pending';
  return (
    <div
      className={`flex items-center px-5 py-3 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors ${isLastRead ? 'bg-primary-50/50 dark:bg-primary-900/5' : ''} ${isSelected ? 'bg-primary-100 dark:bg-primary-900/20' : ''}`}
    >
      {/* 編輯模式 - Checkbox */}
      {isEditMode && (
        <label className="flex items-center mr-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(chapter.id)}
            className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-700"
          />
        </label>
      )}
      <Link to={`/novels/${novelId}/read/${chapter.id}`} className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          {isLastRead && <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500" />}
          <span
            className={`truncate ${isLastRead ? 'text-primary-700 dark:text-primary-400 font-medium' : isFailed ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-700 dark:text-neutral-300'}`}
          >
            第 {chapter.chapter_index} 章 - {chapter.title}
            {isFailed && <span className="ml-2 text-danger-500 text-xs font-medium">失敗</span>}
            {isCrawling && <span className="ml-2 text-primary-500 text-xs">下載中</span>}
            {chapter.word_count === 0 && chapter.status === 'completed' && (
              <span className="ml-2 text-danger-500 text-xs">0字</span>
            )}
          </span>
        </div>
      </Link>
      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
        {/* 管理員編輯章節內容按鈕 (非編輯模式) */}

        {isAdmin && !isEditMode && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onEditContent(chapter);
            }}
            className="btn btn-ghost btn-icon text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400"
            title="編輯章節內容"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
        )}
        {/* 管理員重新抓取按鈕 (非編輯模式, 非上傳小說) */}
        {isAdmin && !isEditMode && !isUploadedNovel && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onRecrawl(chapter);
            }}
            disabled={isRecrawling || isSkipping}
            className="btn btn-ghost btn-icon text-neutral-400 hover:text-warning-600 dark:hover:text-warning-400"
            title="重新抓取此章節"
          >
            {isRecrawling ? (
              <span className="w-4 h-4 border-2 border-warning-500/30 border-t-warning-500 rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
          </button>
        )}
        {/* 管理員跳過按鈕 (非編輯模式, 非上傳小說, 章節未完成時) */}
        {isAdmin && !isEditMode && !isUploadedNovel && chapter.status !== 'completed' && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onSkip(chapter);
            }}
            disabled={isSkipping || isRecrawling}
            className="btn btn-ghost btn-icon text-neutral-400 hover:text-danger-600 dark:hover:text-danger-400"
            title="跳過此章節（標記為失敗，讓整本小說可以結案）"
          >
            {isSkipping ? (
              <span className="w-4 h-4 border-2 border-danger-500/30 border-t-danger-500 rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            )}
          </button>
        )}
        {/* 管理員順序調整按鈕 (非編輯模式) */}
        {isAdmin && !isEditMode && onMoveUp && onMoveDown && (
          <div className="flex flex-col">
            <button
              onClick={(e) => {
                e.preventDefault();
                onMoveUp(chapter);
              }}
              disabled={isFirst}
              className="btn btn-ghost p-0.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="上移"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                onMoveDown(chapter);
              }}
              disabled={isLast}
              className="btn btn-ghost p-0.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="下移"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        )}
        {/* 單一刪除按鈕 (編輯模式, 上傳式小說) */}
        {isAdmin && isEditMode && isUploadedNovel && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onDelete(chapter);
            }}
            className="btn btn-ghost btn-icon text-danger-500 hover:text-danger-600 dark:hover:text-danger-400"
            title="刪除此章節"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
        <span className="body-small w-16 text-right">{chapter.word_count.toLocaleString()} 字</span>
      </div>
    </div>
  );
}
